import { useState, useEffect } from "react";
import { PencilIcon } from "lucide-react";
import doctorService from "@/services/doctorService";
import { UserActionPlanItem } from "@/data/interfaces/UserActionPlan";
import { InsightType } from "@/data/interfaces/InsightType";
import { User } from "@/data/interfaces/User";
import Input from "@/components/UI/Input";
import Select, { SelectOption } from "@/components/UI/Select/Select";
import { ErrorMessage, Tooltip } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";

type ActionPlanRow = UserActionPlanItem & { id?: number };

const UserActionPlanForm = () => {
  const [actionTypes, setActionTypes] = useState<InsightType[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<User | undefined>();
  const [nameSearch, setNameSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [filteredPatients, setFilteredPatients] = useState<User[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [actionPlans, setActionPlans] = useState<ActionPlanRow[]>([
    { insight_type_id: 0, rules: "" },
    { insight_type_id: 0, rules: "" },
    { insight_type_id: 0, rules: "" },
  ]);

  useEffect(() => {
    loadActionTypes();
  }, []);

  useEffect(() => {
    if (selectedPatient) return;

    const hasQuery = nameSearch.length >= 2 || emailSearch.length >= 2;
    if (!hasQuery) {
      setFilteredPatients([]);
      return;
    }

    const timeout = setTimeout(() => {
      searchPatients();
    }, 300);

    return () => clearTimeout(timeout);
  }, [nameSearch, emailSearch, selectedPatient]);

  const loadActionTypes = async () => {
    try {
      const types = await doctorService.getActionTypes();
      setActionTypes(types.filter((type) => type.is_active));
    } catch (error) {
      console.error(error);
      setErrorMessage(`Nepavyko gauti veiksmų tipų: ${error}`);
    }
  };

  const searchPatients = async () => {
    try {
      setIsLoadingPatients(true);
      setErrorMessage(undefined);
      const params: { name?: string; email?: string } = {};
      if (nameSearch.length >= 2) params.name = nameSearch;
      if (emailSearch.length >= 2) params.email = emailSearch;
      const patients = await doctorService.patient.search(params);
      setFilteredPatients(patients);
    } catch (error) {
      console.error(error);
      setErrorMessage(`Nepavyko gauti pacientų sąrašo: ${error}`);
    } finally {
      setIsLoadingPatients(false);
    }
  };

  const emptyPlans: ActionPlanRow[] = [
    { insight_type_id: 0, rules: "" },
    { insight_type_id: 0, rules: "" },
    { insight_type_id: 0, rules: "" },
  ];

  const padToThree = (plans: ActionPlanRow[]): ActionPlanRow[] => {
    const padded = [...plans];
    while (padded.length < 3) {
      padded.push({ insight_type_id: 0, rules: "" });
    }
    return padded;
  };

  const loadPatientPlans = async (patientId: number) => {
    try {
      setIsLoadingPlans(true);
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
      const existingPlans =
        await doctorService.userActionPlan.getByUserId(patientId);
      if (existingPlans.length > 0) {
        setActionPlans(
          padToThree(
            existingPlans.map((p) => ({
              id: p.id,
              insight_type_id: p.insight_type_id,
              rules: p.rules,
            })),
          ),
        );
      } else {
        setActionPlans(emptyPlans);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(`Nepavyko gauti paciento veiksmų planų: ${error}`);
      setActionPlans(emptyPlans);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const selectPatient = async (patient: User) => {
    setSelectedPatient(patient);
    setNameSearch("");
    setEmailSearch("");
    setFilteredPatients([]);
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    await loadPatientPlans(patient.id);
  };

  const updateActionPlan = (
    index: number,
    field: keyof ActionPlanRow,
    value: string | number,
  ) => {
    const updated = [...actionPlans];
    updated[index] = { ...updated[index], [field]: value };
    setActionPlans(updated);
  };

  const handleUpdate = async (index: number) => {
    const plan = actionPlans[index];
    if (!plan.id) return;

    try {
      setIsSubmitting(true);
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
      await doctorService.userActionPlan.update(plan.id, {
        rules: plan.rules,
        insight_type_id: plan.insight_type_id,
      });
      setSuccessMessage("Veiksmų planas sėkmingai atnaujintas!");
      setEditingIndex(null);
    } catch (error) {
      console.error(error);
      setErrorMessage(`Nepavyko atnaujinti veiksmų plano: ${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!selectedPatient) return;

    try {
      setIsSubmitting(true);
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
      await doctorService.userActionPlan.deleteAll(selectedPatient.id);
      setSuccessMessage("Visi veiksmų planai sėkmingai ištrinti!");
      setEditingIndex(null);
      await loadPatientPlans(selectedPatient.id);
    } catch (error) {
      console.error(error);
      setErrorMessage(`Nepavyko ištrinti veiksmų planų: ${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPatient) {
      setErrorMessage("Prašome pasirinkti pacientą");
      return;
    }

    const validPlans = newPlans.filter(
      (plan) => plan.insight_type_id > 0 && plan.rules.trim() !== "",
    );

    try {
      setIsSubmitting(true);
      setErrorMessage(undefined);
      setSuccessMessage(undefined);

      const createdPlans = await doctorService.userActionPlan.create({
        user_id: selectedPatient.id,
        items: validPlans,
      });

      setSuccessMessage("Veiksmų planai sėkmingai sukurti!");
      setActionPlans(
        padToThree(
          createdPlans.map((p) => ({
            id: p.id,
            insight_type_id: p.insight_type_id,
            rules: p.rules,
          })),
        ),
      );
    } catch (error) {
      console.error(error);
      setErrorMessage(`Nepavyko sukurti veiksmų planų: ${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeName = (typeId: number) =>
    actionTypes.find((t) => t.id === typeId)?.name ?? "—";

  const actionTypeOptions: SelectOption[] = actionTypes.map((type) => ({
    label: type.name,
    value: String(type.id),
  }));

  const existingPlans = actionPlans.filter((p) => p.id);
  const newPlans = actionPlans
    .map((p, i) => ({ ...p, originalIndex: i }))
    .filter((p) => !p.id);

  const filledNewPlans = newPlans.filter(
    (p) => p.insight_type_id > 0 && p.rules.trim() !== "",
  );
  const allNewPlansFilled =
    newPlans.length > 0 && filledNewPlans.length === newPlans.length;

  const getSubmitTooltip = () => {
    const missingType = newPlans.some((p) => p.insight_type_id === 0);
    const missingRules = newPlans.some((p) => p.rules.trim() === "");
    if (missingType && missingRules)
      return "Užpildykite visų planų tipus ir taisykles";
    if (missingType) return "Pasirinkite tipą kiekvienam planui";
    if (missingRules) return "Įveskite taisykles kiekvienam planui";
    return "";
  };

  return (
    <div className="flex gap-8">
      {/* Sukurti veiksmų planus pacientui */}
      <div className="flex w-1/3 shrink-0 flex-col gap-4">
        <h3 className="text-center text-lg font-semibold">
          Sukurti veiksmų planus pacientui
        </h3>

        <div className="relative flex flex-col gap-2">
          {!selectedPatient && (
            <>
              <Input
                placeholder="Įveskite paciento vardą..."
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
              />
              <Input
                placeholder="Įveskite paciento el. paštą..."
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                isLoading={isLoadingPatients}
              />

              {(nameSearch.length >= 2 || emailSearch.length >= 2) && (
                <div className="absolute top-full z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                  {filteredPatients.length > 0 ? (
                    filteredPatients.map((user) => (
                      <div
                        key={user.id}
                        className="cursor-pointer border-b border-gray-200 p-3 last:border-b-0 hover:bg-gray-100"
                        onClick={() => selectPatient(user)}
                      >
                        <div className="font-medium">
                          {user.name} {user.surname}
                        </div>
                        <div className="text-sm text-gray-600">
                          {user.email}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-center text-gray-500">
                      Nerastas vartotojas
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {selectedPatient && (
            <>
              <div className="rounded-xl border border-green-300 bg-green-50 p-3">
                <div className="flex justify-between gap-2">
                  <div>
                    <div className="font-medium">
                      {selectedPatient.name} {selectedPatient.surname}
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedPatient.email}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPatient(undefined);
                      setActionPlans(emptyPlans);
                      setErrorMessage(undefined);
                      setSuccessMessage(undefined);
                    }}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Atšaukti
                  </button>
                </div>
              </div>

              {!isLoadingPlans && newPlans.length > 0 && (
                <Tooltip
                  content={!allNewPlansFilled ? getSubmitTooltip() : ""}
                  disabled={allNewPlansFilled}
                >
                  <Button
                    type="submit"
                    disabled={isSubmitting || !allNewPlansFilled}
                    onClick={handleSubmit}
                    className="w-full text-sm"
                  >
                    {isSubmitting ? "Kuriama..." : "Sukurti veiksmų planus"}
                  </Button>
                </Tooltip>
              )}
            </>
          )}

          <ErrorMessage error={errorMessage} />
          {successMessage && (
            <div className="rounded-xl border border-green-300 bg-green-50 p-3 text-green-800">
              {successMessage}
            </div>
          )}
        </div>
      </div>

      {selectedPatient && (
        <div className="flex w-2/3 flex-col gap-4">
          <h3 className="text-center text-lg font-semibold">
            {existingPlans.length > 0
              ? "Priskirti veiksmų planai"
              : "Priskirti veiksmų planus"}
          </h3>

          {isLoadingPlans ? (
            <div className="text-center text-gray-500">
              Kraunami veiksmų planai...
            </div>
          ) : existingPlans.length > 0 ? (
            <div className="flex flex-col gap-4">
              {actionPlans.map((plan, index) => {
                if (!plan.id) return null;
                const isEditing = editingIndex === index;

                if (!isEditing) {
                  return (
                    <div
                      key={plan.id}
                      className="rounded-xl border p-4 transition-colors hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="text-xl font-medium">
                            {getTypeName(plan.insight_type_id)}
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {plan.rules}
                          </p>
                        </div>

                        <div className="ml-auto flex shrink-0 items-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={isSubmitting}
                            onClick={() => {
                              setEditingIndex(index);
                              setErrorMessage(undefined);
                              setSuccessMessage(undefined);
                            }}
                          >
                            <PencilIcon />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={plan.id}
                    className="rounded-xl border border-gray-300 bg-gray-50 p-4"
                  >
                    <div className="flex flex-col gap-3">
                      <Select
                        label=""
                        options={actionTypeOptions}
                        value={
                          plan.insight_type_id > 0
                            ? String(plan.insight_type_id)
                            : undefined
                        }
                        onValueChange={(value) =>
                          updateActionPlan(
                            index,
                            "insight_type_id",
                            Number(value),
                          )
                        }
                        placeholder="Pasirinkite tipą..."
                      />

                      <Input
                        label=""
                        placeholder="Įveskite taisykles..."
                        value={plan.rules}
                        onChange={(e) =>
                          updateActionPlan(index, "rules", e.target.value)
                        }
                      />

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={isSubmitting}
                          onClick={() => handleUpdate(index)}
                        >
                          {isSubmitting ? "Saugoma..." : "Išsaugoti"}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setEditingIndex(null)}
                        >
                          Atšaukti
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              <Button
                variant="destructive"
                disabled={isSubmitting}
                onClick={handleDeleteAll}
                className="w-full text-sm"
              >
                {isSubmitting ? "Trinami..." : "Ištrinti visus veiksmų planus"}
              </Button>
            </div>
          ) : null}

          {!isLoadingPlans && newPlans.length > 0 && (
            <div className="flex flex-col gap-4">
              {newPlans.map((plan) => (
                <div
                  key={`new-${plan.originalIndex}`}
                  className="rounded-xl border border-gray-300 bg-gray-50 p-4"
                >
                  <div className="flex flex-col gap-3">
                    <Select
                      label=""
                      options={actionTypeOptions}
                      value={
                        plan.insight_type_id > 0
                          ? String(plan.insight_type_id)
                          : undefined
                      }
                      onValueChange={(value) =>
                        updateActionPlan(
                          plan.originalIndex,
                          "insight_type_id",
                          Number(value),
                        )
                      }
                      placeholder="Pasirinkite tipą..."
                    />

                    <Input
                      label=""
                      placeholder="Įveskite taisykles..."
                      value={plan.rules}
                      onChange={(e) =>
                        updateActionPlan(
                          plan.originalIndex,
                          "rules",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserActionPlanForm;
